/**
 * Orca Account Web - Profile Module
 * Handles profile management and account security
 */

import { createClient } from '@supabase/supabase-js';

// Configuration
const CONFIG = {
  supabaseUrl: import.meta.env.VITE_SUPABASE_URL || 'https://api.orcaslicer.com',
  supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || ''
};

// Initialize Supabase client
let supabase;
try {
  supabase = createClient(CONFIG.supabaseUrl, CONFIG.supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true
    }
  });
} catch (error) {
  console.error('Failed to initialize Supabase client:', error);
}

// DOM Elements
const profileForm = document.getElementById('profile-form');
const displayNameInput = document.getElementById('display-name');
const userEmailInput = document.getElementById('user-email');
const userBioInput = document.getElementById('user-bio');
const changePasswordBtn = document.getElementById('change-password-btn');
const manageAccountsBtn = document.getElementById('manage-accounts-btn');
const deleteAccountBtn = document.getElementById('delete-account-btn');
const signoutBtn = document.getElementById('signout-btn');

// Modal elements
const passwordModal = document.getElementById('password-modal');
const closePasswordModal = document.getElementById('close-password-modal');
const cancelPasswordChange = document.getElementById('cancel-password-change');
const changePasswordForm = document.getElementById('change-password-form');

// Message element
const messageBox = document.getElementById('message');

// State
let currentUser = null;
let userProfile = null;

/**
 * Initialize profile page
 */
async function initProfile() {
  if (!supabase) {
    showError('Failed to initialize profile page. Please refresh the page.');
    return;
  }

  // Check authentication
  const { data: { session }, error } = await supabase.auth.getSession();

  if (error || !session) {
    // Redirect to sign in page
    window.location.href = '/';
    return;
  }

  currentUser = session.user;

  // Set up event listeners
  setupEventListeners();

  // Load profile data
  await loadProfileData();

  // Listen for auth changes
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') {
      window.location.href = '/';
    }
  });
}

/**
 * Set up event listeners
 */
function setupEventListeners() {
  // Profile form
  if (profileForm) {
    profileForm.addEventListener('submit', handleProfileUpdate);
  }

  // Security buttons
  if (changePasswordBtn) {
    changePasswordBtn.addEventListener('click', showPasswordModal);
  }

  if (manageAccountsBtn) {
    manageAccountsBtn.addEventListener('click', handleManageAccounts);
  }

  if (deleteAccountBtn) {
    deleteAccountBtn.addEventListener('click', handleDeleteAccount);
  }

  if (signoutBtn) {
    signoutBtn.addEventListener('click', handleSignOut);
  }

  // Modal controls
  if (closePasswordModal) {
    closePasswordModal.addEventListener('click', hidePasswordModal);
  }

  if (cancelPasswordChange) {
    cancelPasswordChange.addEventListener('click', hidePasswordModal);
  }

  if (changePasswordForm) {
    changePasswordForm.addEventListener('submit', handlePasswordChange);
  }

  // Close modal on backdrop click
  const modalBackdrop = passwordModal?.querySelector('.modal-backdrop');
  if (modalBackdrop) {
    modalBackdrop.addEventListener('click', hidePasswordModal);
  }

  // Close modal on escape key
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !passwordModal?.classList.contains('hidden')) {
      hidePasswordModal();
    }
  });
}

/**
 * Load profile data
 */
async function loadProfileData() {
  try {
    // Load user profile from database
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', currentUser.id)
      .single();

    if (error && error.code !== 'PGRST116') { // Ignore "not found" error
      throw error;
    }

    userProfile = data;
    populateProfileForm();

  } catch (error) {
    console.error('Failed to load profile data:', error);
    showError('Failed to load profile data. Please refresh the page.');
  }
}

/**
 * Populate profile form with existing data
 */
function populateProfileForm() {
  if (displayNameInput) {
    displayNameInput.value = userProfile?.display_name || '';
  }

  if (userEmailInput) {
    userEmailInput.value = currentUser.email || '';
  }

  if (userBioInput) {
    userBioInput.value = userProfile?.bio || '';
  }
}

/**
 * Handle profile update
 */
async function handleProfileUpdate(event) {
  event.preventDefault();

  if (!supabase) return;

  const form = event.target;
  const formData = new FormData(form);

  const displayName = formData.get('display_name')?.trim();
  const bio = formData.get('bio')?.trim();

  // Validate inputs
  if (displayName && displayName.length > 100) {
    showFieldError(displayNameInput, 'Display name must be less than 100 characters');
    return;
  }

  if (bio && bio.length > 500) {
    showFieldError(userBioInput, 'Bio must be less than 500 characters');
    return;
  }

  setFormLoading(form, true);
  clearAllFieldErrors();
  hideMessage();

  try {
    const updateData = {};
    if (displayName !== undefined) updateData.display_name = displayName || null;
    if (bio !== undefined) updateData.bio = bio || null;

    // Update or insert profile
    const { data, error } = await supabase
      .from('profiles')
      .upsert({
        id: currentUser.id,
        ...updateData,
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) {
      throw error;
    }

    userProfile = data;
    showSuccess('Profile updated successfully!');

  } catch (error) {
    console.error('Profile update error:', error);
    showError('Failed to update profile. Please try again.');
  } finally {
    setFormLoading(form, false);
  }
}

/**
 * Show password modal
 */
function showPasswordModal() {
  if (passwordModal) {
    passwordModal.classList.remove('hidden');
    // Focus first input
    const firstInput = passwordModal.querySelector('input');
    if (firstInput) {
      setTimeout(() => firstInput.focus(), 100);
    }
  }
}

/**
 * Hide password modal
 */
function hidePasswordModal() {
  if (passwordModal) {
    passwordModal.classList.add('hidden');
    changePasswordForm?.reset();
    clearAllFieldErrors();
  }
}

/**
 * Handle password change
 */
async function handlePasswordChange(event) {
  event.preventDefault();

  if (!supabase) return;

  const form = event.target;
  const formData = new FormData(form);

  const currentPassword = formData.get('current_password');
  const newPassword = formData.get('new_password');
  const confirmPassword = formData.get('confirm_password');

  // Validate passwords
  if (newPassword.length < 8) {
    showFieldError(form.new_password, 'Password must be at least 8 characters long');
    return;
  }

  if (newPassword !== confirmPassword) {
    showFieldError(form.confirm_password, 'Passwords do not match');
    return;
  }

  setFormLoading(form, true);
  clearAllFieldErrors();

  try {
    // Verify current password by attempting to sign in
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: currentUser.email,
      password: currentPassword
    });

    if (signInError) {
      showFieldError(form.current_password, 'Current password is incorrect');
      return;
    }

    // Update password
    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (updateError) {
      throw updateError;
    }

    showSuccess('Password changed successfully!');
    hidePasswordModal();

  } catch (error) {
    console.error('Password change error:', error);
    showError('Failed to change password. Please try again.');
  } finally {
    setFormLoading(form, false);
  }
}

/**
 * Handle manage accounts
 */
function handleManageAccounts() {
  // For now, show info message
  showInfo('Connected accounts management will be available in a future update.');
}

/**
 * Handle delete account
 */
async function handleDeleteAccount() {
  const confirmed = confirm(
    'Are you sure you want to delete your account? This action cannot be undone and will permanently delete:\n\n' +
    '• Your profile information\n' +
    '• Your settings and preferences\n' +
    '• Your account data\n\n' +
    'Type "DELETE" to confirm:'
  );

  if (!confirmed) return;

  const confirmation = prompt('Please type "DELETE" to confirm account deletion:');
  if (confirmation !== 'DELETE') {
    showInfo('Account deletion cancelled.');
    return;
  }

  try {
    // Delete profile data
    const { error: profileError } = await supabase
      .from('profiles')
      .delete()
      .eq('id', currentUser.id);

    if (profileError) {
      throw profileError;
    }

    // Delete user account
    const { error: userError } = await supabase.auth.admin.deleteUser(currentUser.id);

    if (userError) {
      throw userError;
    }

    showInfo('Account deleted successfully. Redirecting...');
    setTimeout(() => {
      window.location.href = '/';
    }, 2000);

  } catch (error) {
    console.error('Account deletion error:', error);
    showError('Failed to delete account. Please contact support for assistance.');
  }
}

/**
 * Handle sign out
 */
async function handleSignOut() {
  try {
    if (!supabase) return;

    await supabase.auth.signOut();
    showSuccess('Signed out successfully. Redirecting...');

    // Redirect will be handled by auth state change listener

  } catch (error) {
    console.error('Sign out error:', error);
    showError('Failed to sign out. Please try again.');
  }
}

/**
 * Set form loading state
 */
function setFormLoading(form, loading) {
  const submitButton = form.querySelector('button[type="submit"]');
  const buttonText = submitButton.querySelector('.btn-text');
  const spinner = submitButton.querySelector('.btn-spinner');

  if (loading) {
    submitButton.disabled = true;
    submitButton.classList.add('loading');
    if (buttonText) buttonText.textContent = 'Processing...';
  } else {
    submitButton.disabled = false;
    submitButton.classList.remove('loading');

    // Restore original text based on form
    if (form.id === 'profile-form') {
      buttonText.textContent = 'Save Profile';
    } else if (form.id === 'change-password-form') {
      buttonText.textContent = 'Change Password';
    }
  }
}

/**
 * Show field error
 */
function showFieldError(field, message) {
  field.classList.add('error');
  const errorId = field.id + '-error';
  const errorElement = document.getElementById(errorId);
  if (errorElement) {
    errorElement.textContent = message;
    errorElement.classList.add('is-visible');
  }
}

/**
 * Clear field error
 */
function clearFieldError(field) {
  field.classList.remove('error');
  const errorId = field.id + '-error';
  const errorElement = document.getElementById(errorId);
  if (errorElement) {
    errorElement.classList.remove('is-visible');
  }
}

/**
 * Clear all field errors
 */
function clearAllFieldErrors() {
  document.querySelectorAll('.form-input.error').forEach(field => {
    clearFieldError(field);
  });
}

/**
 * Show success message
 */
function showSuccess(text) {
  showMessage(text, 'success');
}

/**
 * Show error message
 */
function showError(text) {
  showMessage(text, 'error');
}

/**
 * Show info message
 */
function showInfo(text) {
  showMessage(text, 'info');
}

/**
 * Show message
 */
function showMessage(text, variant = 'info') {
  if (!messageBox) return;

  messageBox.textContent = text;
  messageBox.dataset.variant = variant;
  messageBox.classList.add('is-visible');

  // Auto-hide success and info messages after 5 seconds
  if (variant === 'success' || variant === 'info') {
    setTimeout(() => {
      hideMessage();
    }, 5000);
  }
}

/**
 * Hide message
 */
function hideMessage() {
  if (!messageBox) return;

  messageBox.classList.remove('is-visible');
  messageBox.textContent = '';
  messageBox.removeAttribute('data-variant');
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', initProfile);